import { useEffect, useState } from 'react';
import { Button } from '@components/button';
import { PaymentRequestPostResponse, QuoteGetResponse } from '@lio/api-types';
import { QuotePaymentStatus } from '@lio/rule-engine';
import { MakePaymentModal } from '@pages/Payments/NewPayment/ReviewAndPayStep/MakePaymentModal';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useCreateBillingAccountPayerRecord } from '@services/billing-account/useCreateBillingAccountPayerRecord';
import { ToastType } from '@components/toast/ToastType.enum';
import { useSnackbar } from 'notistack';
import { useCreatePaymentRequest } from '@services/payments/useCreatePaymentRequest';
import { PaymentScripts } from '@pages/Payments/NewPayment/ReviewAndPayStep/PaymentScripts';
import { writingCompanyIds } from '@models/productOffering';
import { PaymentTransactionResult } from '@models/payment/paymentTransactionResult.type';
import { SubmissionSummary } from '@pages/BindQuote/SubmissionSummary/SubmissionSummary';
import { useAlertStore } from '@store/AlertStore';

export type PayAndBindButtonProps = {
  onHandleBindQuote: () => void;
  isLoading: boolean;
  quote: QuoteGetResponse;
  disabled?: boolean;
  onSaveQuote: ({ exitOnSave }: { exitOnSave?: boolean }) => void;
};

export const PayAndBindButton = ({
  disabled,
  onHandleBindQuote,
  onSaveQuote,
  quote,
  isLoading,
}: PayAndBindButtonProps) => {
  // temporary button since LIO-19913 is not done
  const isPaymentDisabled = useAlertStore((state) => state.isPaymentDisabled, shallow);
  const quoteShouldShowPaymentDisabled = isPaymentDisabled && quote?.offering?.requirePreBindPayment ? true : false;

  const { enqueueSnackbar } = useSnackbar();
  const [currentUser] = useStore((state) => [state.currentUser], shallow);
  const [delayedIsLoading, setDelayedLoading] = useState(false);
  const [openSubmissionSummary, setOpenSubmissionSummary] = useState(false);
  const [acknowledgeForms, setAcknowledgeForms] = useState(false);
  const [paymentRequestResult, setPaymentRequestResult] = useState<PaymentRequestPostResponse>();

  const { createBillingAccountPayerRecord, isPending: isCreating } = useCreateBillingAccountPayerRecord({
    userId: currentUser!.id,
    displayToasts: false,
  });
  const { createPaymentRequest, isPending: isCreatingRequest } = useCreatePaymentRequest();

  const handlePaymentModalClose = ({ transactionData }: { transactionData?: PaymentTransactionResult }) => {
    if (!transactionData) {
      setPaymentRequestResult(undefined);
      return;
    }

    onHandleBindQuote();
    setPaymentRequestResult(undefined);
  };

  useEffect(() => {
    if (!isCreatingRequest) {
      setTimeout(() => {
        // one inc modal takes awhile to open, so lets keep the loading spinner going for a more seamless transition for the user
        setDelayedLoading(false);
      }, 3000);
    }
  }, [isCreatingRequest]);

  const isButtonLoading = isLoading || isCreating || isCreatingRequest || delayedIsLoading;
  const isButtonDisabled = disabled || !!paymentRequestResult; // disabled if one inc modal is open
  const isPaymentComplete = quote?.paymentStatus === QuotePaymentStatus.Complete;

  const handleContinuePayment = async () => {
    if (!acknowledgeForms) return;

    setOpenSubmissionSummary(false);
    if (quote?.paymentStatus === QuotePaymentStatus.Complete) {
      onHandleBindQuote();
      return;
    }

    let payerId = currentUser?.payer?.id;
    if (!currentUser?.payer?.id) {
      const payerRecord = await createBillingAccountPayerRecord({} as any); // TODO: wont have to do once types are up to date
      if (!payerRecord?.payer?.id) {
        enqueueSnackbar('An error occurred, please try again or contact support', {
          variant: ToastType.Info,
        });
        return;
      }

      payerId = payerRecord.payer.id;
    }

    const formatPolicyNumber = (policyNumber: string) => {
      return `${quote.writingCompany?.id === writingCompanyIds.specialty ? 'LSIC' : 'LIO'}${policyNumber}-00`;
    };

    const paymentJson: any = {
      payerId,
      policyPayments:
        quote.multiPolicyQuoteDestinations && quote.multiPolicyQuoteDestinations.length > 0
          ? quote.multiPolicyQuoteDestinations.map((qd) => ({
              policyNumber: formatPolicyNumber(qd.policyNumber?.policyNumber),
              paymentAmount: qd.termPremium,
            }))
          : [
              {
                policyNumber: formatPolicyNumber(quote.policyNumber?.policyNumber ?? ''),
                paymentAmount: quote.termPremium,
              },
            ],
    };

    setDelayedLoading(true);
    const result = await createPaymentRequest(paymentJson);
    setPaymentRequestResult(result);
  };
  return (
    <>
      <PaymentScripts />
      <Button
        disabled={isButtonDisabled || (!isPaymentComplete && quoteShouldShowPaymentDisabled)}
        isLoading={isButtonLoading}
        variant='contained'
        onClick={async () => {
          await onSaveQuote({ exitOnSave: false });
          setOpenSubmissionSummary(true);
        }}
      >
        {isPaymentComplete ? 'Bind & Generate Policy' : 'Pay & Bind'}
      </Button>

      <SubmissionSummary
        quoteId={quote.id}
        open={openSubmissionSummary}
        handleClose={() => setOpenSubmissionSummary(false)}
        setAcknowledgeForms={setAcknowledgeForms}
        acknowledgeForms={acknowledgeForms}
        handleContinuePayment={handleContinuePayment}
        payAndBind
        isPaymentDisabled={quoteShouldShowPaymentDisabled}
      />

      {paymentRequestResult && (
        <MakePaymentModal open={true} paymentRequest={paymentRequestResult} onClose={handlePaymentModalClose} />
      )}
    </>
  );
};
